import { createContext, useState, useMemo } from "react";
import HumanResourceLayout from "../../../components/layouts/HumanResourceLayout";
import { Helmet } from "react-helmet-async";
import {
	IoIosAddCircleOutline,
	IoIosRemoveCircleOutline,
} from "react-icons/io";
import { HiDotsHorizontal } from "react-icons/hi";
import { Avatar } from "@mui/material";
import { OrganizationChart } from "primereact/organizationchart";
import { APP_CONFIG } from "../../../helpers/appHelpers";
import AddNodeModal from "./modals/AddNodeModal";
import {
	useOrganizationTreeStore,
	TreeNode,
} from "../../../state-management/useOrganizationTreeStore";
import { JobTitleOrderBy } from "../../../models/job-titles.dto";
import { useGetEmployee } from "../../../services/employee.service";
import { useGetJobTitle } from "../../../services/job-title.service";
import { EmployeeOrderBy } from "../../../models/employee.dto";
import SkewLoader from "react-spinners/SkewLoader";
import NodeMenu from "./modals/NodeMenuModal";
import useUserStore from "../../../state-management/useUserStore";
import { useAsyncEffect, useLockFn } from "ahooks";
import API from "../../../services/utils/api";
import PayrollCompanyLayout from "../../../components/layouts/PayrollCompanyLayout";
import useCompanyStore from "../../../state-management/useCompanyStore";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

export const TreeContext = createContext<() => void>(() => {});

const OrganisationTree = () => {
	const { userType } = useUserStore();
	const LAYOUT = useMemo(() => {
		if (userType === "ORGANIZATION") {
			return PayrollCompanyLayout;
		} else {
			return HumanResourceLayout;
		}
	}, [userType]);
	const { currentCompany } = useCompanyStore();
	const { organizationTree, treeSourceData, levelColors, generateTree } =
		useOrganizationTreeStore();
	const [loadingTree, setLoadingTree] = useState(false);
	const [selection, setSelection] = useState([]);
	const [selectedNode, setSelectedNode] = useState<TreeNode>();
	const [addNodeEl, setAddNodeEl] = useState<any>(null);
	const [nodeMenuEl, setNodeMenuEl] = useState<any>(null);

	useAsyncEffect(
		useLockFn(async () => {
			setLoadingTree(true);

			try {
				const { data } = await API.HrAPI.getCompanyTree(
					currentCompany?.id as number
				);
				generateTree(data);
				setLoadingTree(false);
			} catch (error: any) {
			} finally {
				setLoadingTree(false);
			}
		}),
		[]
	);

	const { data: jobTitles } = useGetJobTitle({
		companyId: currentCompany?.id,
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: JobTitleOrderBy.NAME_ASC,
	});

	const { data: employees } = useGetEmployee({
		companyId: currentCompany?.id,
		status: "ACTIVE",
		limit: APP_CONFIG.PAGE_LIMIT,
		orderBy: EmployeeOrderBy.FIRST_NAME_ASC,
	});

	const nodeTemplate = (node: TreeNode) => {
		const colorCode = levelColors.find(
			(colorObj) => colorObj.parendId === node.parentId
		);

		return (
			<div
				className="w-[200px] rounded-xl bg-[#F2F2F4] relative flex flex-col
                font-inter text-[11px] leading-[1.4] text-[#292D30]"
			>
				<IoIosAddCircleOutline
					onClick={(e) => {
						setSelectedNode(node);
						setAddNodeEl(e.currentTarget);
					}}
					className="absolute -right-[35px] top-1/2 -translate-y-1/2 text-[30px] text-primary-blue cursor-pointer"
				/>
				<HiDotsHorizontal
					onClick={(e) => {
						setSelectedNode(node);
						setNodeMenuEl(e.currentTarget);
					}}
					className="absolute top-2 right-2 text-[20px] cursor-pointer"
				/>
				<div className="px-4 py-3.5 mr-auto flex items-center gap-2.5">
					<div>
						<Avatar
							src={node.data?.employee?.photoUrl}
							alt={node.data?.employee?.firstName}
							className="w-8 h-8 object-cover"
						/>
					</div>
					{node.data.employee ? (
						<p className="font-bold max-w-[80px] truncate">
							{node.data?.employee?.firstName +
								" " +
								node.data?.employee?.lastName}
						</p>
					) : (
						<p className="text-primary-blue">Assign Employee</p>
					)}
				</div>
				<div
					className="w-full p-1 rounded-b-xl text-center font-normal text-white"
					style={{ background: colorCode?.levelColor }}
				>
					{node.data.jobTitle?.name}
				</div>
			</div>
		);
	};

	return (
		<LAYOUT>
			<Helmet>
				<title>
					Organisation Tree |{userType === "HR" ? " HR " : " Company "}Flow{" "}
				</title>
			</Helmet>

			<div className="flex justify-between items-center">
				<h1 className="font-bold text-xl text-charcoal">Organisation Tree</h1>
			</div>
			<div className="bg-white overflow-hidden h-[750px] rounded-lg border border-gray-100 mt-6 pb-3 relative">
				<div className="flex justify-between items-center px-4 py-3 bg-white shadow-md">
					<h3 className="text-2xs relative">
						Organisation,{" "}
						<span className="font-bold">
							Total {treeSourceData.length} people
						</span>
					</h3>
					<div className="relative z-[99999999]">
						{/* <Search fallbackData={[]} setTableData={setTableData} /> */}
					</div>
				</div>
				<div className="grow flex flex-col items-center px-4 mt-8 mb-32 relative h-full">
					{loadingTree && organizationTree.length < 1 ? (
						<div className="grow place-content-center py-[200px] text-center my-auto">
							<div className="flex items-center gap-4">
								<span className="font-medium text-[18px]">Generating Tree</span>
								<SkewLoader color="#F58B00" />
							</div>
						</div>
					) : (
						<>
							{organizationTree.length ? (
								<TransformWrapper
									initialScale={0.2}
									minScale={0.2}
									maxScale={4}
									centerOnInit={true}
									wheel={{ step: 0.1 }}
									doubleClick={{ disabled: false, step: 0.3 }}
									limitToBounds={false}
									centerZoomedOut={false}
									alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
								>
									{({ zoomIn, zoomOut, resetTransform }) => (
										<>
											{/* Zoom Controls */}
											<div className="absolute top-4 right-4 z-50 flex flex-col gap-2 bg-white rounded-lg shadow-md p-2">
												<button
													onClick={() => zoomIn()}
													className="p-2 hover:bg-gray-100 rounded text-gray-600"
													title="Zoom In"
												>
													<IoIosAddCircleOutline className="text-xl" />
												</button>
												<button
													onClick={() => zoomOut()}
													className="p-2 hover:bg-gray-100 rounded text-gray-600"
													title="Zoom Out"
												>
													<IoIosRemoveCircleOutline className="text-xl" />
												</button>
												<button
													onClick={() => resetTransform()}
													className="p-2 hover:bg-gray-100 rounded text-gray-600 text-xs"
													title="Reset Zoom"
												>
													Reset
												</button>
											</div>

											<TransformComponent
												wrapperClass="w-full h-full"
												contentClass="w-full h-full flex justify-center items-center"
												wrapperStyle={{
													width: "100%",
													height: "100%",
													overflow: "hidden",
												}}
											>
												<div className="p-20">
													<OrganizationChart
														value={organizationTree}
														className="mt-6"
														selection={selection}
														nodeTemplate={nodeTemplate}
														onSelectionChange={(e: any) =>
															setSelection(e?.data)
														}
														style={{
															minHeight: "600px",
															minWidth: "800px",
														}}
													/>
												</div>
											</TransformComponent>
										</>
									)}
								</TransformWrapper>
							) : (
								<div className="flex items-center justify-center w-full h-full">
									<button
										className="flex items-center space-x-2 px-4 py-3 rounded-lg bg-[#F58B00] text-white text-sm font-medium shadow hover:bg-[#cb7504] transition"
										onClick={(e) => setAddNodeEl(e.currentTarget)}
									>
										<IoIosAddCircleOutline className="text-xl" />
										<span>Add Organisation Tree</span>
									</button>
								</div>
							)}
						</>
					)}
				</div>

				{addNodeEl && (
					<AddNodeModal
						anchorEl={addNodeEl}
						nodeParentId={selectedNode?.id as number}
						jobTitles={jobTitles?.data || []}
						employees={
							employees?.data?.filter((employee) => {
								return !treeSourceData.find(
									(node) => node.data.employeeId === employee.id
								);
							}) || []
						}
						handleClose={() => setAddNodeEl(null)}
					/>
				)}

				{nodeMenuEl && (
					<NodeMenu
						anchorEl={nodeMenuEl}
						node={selectedNode as TreeNode}
						jobTitles={jobTitles?.data || []}
						employees={employees?.data || []}
						handleClose={() => setNodeMenuEl(null)}
					/>
				)}
			</div>
		</LAYOUT>
	);
};

export default OrganisationTree;
